import { useEffect, useRef, useState } from 'react';
import { RENDERED_SLIDES_BUCKET } from '../../lib/storage';
import type { SlideRead } from '../../types/api';
import { useSignedAssetUrl } from '../knowledge-base/useSignedAssetUrl';

interface SlideImageProps {
  slide: SlideRead;
  /** `stage` for the large preview, `thumb` for the strip below it. */
  variant: 'stage' | 'thumb';
  /**
   * The render has been outstanding long enough to be worth mentioning. Only
   * changes the words — a slow render is not an error.
   */
  isSlow?: boolean;
}

/**
 * One rendered slide, read out of the private `rendered-slides` bucket through a
 * short-lived signed URL — the same signing path as the asset previews, pointed
 * at a different bucket.
 *
 * `rendered_path` is null while the design agent is drawing this slide, which is
 * also the state a layer edit puts it back into. That is a placeholder, never an
 * error: the slide exists, the picture is just not out of the oven yet.
 */
export function SlideImage({ slide, variant, isSlow = false }: SlideImageProps) {
  const { url, isLoading, error } = useSignedAssetUrl(slide.rendered_path, RENDERED_SLIDES_BUCKET);
  const className = `slide-image slide-image--${variant}`;
  const label = `Slide ${slide.slide_index + 1}`;

  if (slide.rendered_path === null) {
    return (
      <div className={`${className} slide-image--pending`}>
        <span className="slide-image__note" role="status">
          {isSlow ? 'Still rendering' : 'Rendering'}
        </span>
        {variant === 'stage' ? (
          <span className="slide-image__headline">{slide.headline}</span>
        ) : null}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={className}>
        <span className="slide-image__note">Loading</span>
      </div>
    );
  }

  if (error || !url) {
    return (
      <div className={className}>
        <span className="slide-image__note">Preview unavailable</span>
      </div>
    );
  }

  return (
    <div className={className}>
      <FadeInImage key={url} src={url} alt={`${label}: ${slide.headline}`} />
    </div>
  );
}

interface FadeInImageProps {
  src: string;
  alt: string;
}

/**
 * The picture arrives rather than pops: hidden until the bytes are in, then a
 * short CSS fade. Keyed on the URL by the caller, so a re-render (a new signed
 * URL, a layer edit) starts hidden again. A picture the cache answers before
 * the load handler is attached is caught by the `complete` check on mount.
 */
function FadeInImage({ src, alt }: FadeInImageProps) {
  const ref = useRef<HTMLImageElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (ref.current?.complete) setIsLoaded(true);
  }, []);

  return (
    <img
      className={isLoaded ? 'slide-image__img slide-image__img--loaded' : 'slide-image__img'}
      ref={ref}
      src={src}
      alt={alt}
      onLoad={() => setIsLoaded(true)}
    />
  );
}
